/**
 * Cross-Domain Authentication Utility
 *
 * Handles authentication transfer between different apps (e.g., user-dashboard and qual-analyzer)
 * that run on different origins (different ports in development, different subdomains in production).
 *
 * Strategy:
 * - Development (different ports): Uses URL query parameters to pass tokens
 * - Production (subdomains): Uses cross-subdomain cookies
 */
const CROSS_DOMAIN_COOKIE_NAME = 'bri_cross_domain_auth';
const CROSS_DOMAIN_PARAM_NAME = 'auth_token';
const CROSS_DOMAIN_WORKSPACE_PARAM_NAME = 'workspace_id';
const TOKEN_KEY = 'bri_auth_token';
const REFRESH_TOKEN_KEY = 'bri_auth_refresh_token';
const IMPERSONATION_MODE_KEY = 'bri_impersonation_mode';
const TARGET_WORKSPACE_KEY = 'bri_cross_domain_workspace_id';
const TOKEN_EXPIRY_MINUTES = 5; // Token transfer window
function getTokenPayload(token) {
    try {
        const parts = token.split('.');
        if (parts.length !== 3)
            return null;
        return JSON.parse(atob(parts[1]));
    }
    catch (error) {
        console.error('CrossDomainAuth: Error parsing token payload:', error);
        return null;
    }
}
function isImpersonationToken(token) {
    return getTokenPayload(token)?.tokenType === 'impersonation';
}
function buildTransferData(token, refreshToken, workspaceId) {
    return {
        token,
        refreshToken: refreshToken || undefined,
        workspaceId: workspaceId || undefined,
        timestamp: Date.now(),
        sourceApp: typeof window !== 'undefined' ? window.location.origin : 'unknown',
    };
}
/**
 * Determines if we should use URL parameters for auth transfer
 * Returns true for:
 * - localhost (different ports can't share cookies)
 * - CloudFront URLs (different distributions can't share cookies)
 * - Any domains that don't share a common parent domain
 */
function shouldUseUrlParams() {
    if (typeof window === 'undefined')
        return true;
    const hostname = window.location.hostname;
    // Localhost always uses URL params (cookies don't work across ports)
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return true;
    }
    // CloudFront URLs use URL params (different distributions can't share cookies)
    if (hostname.endsWith('.cloudfront.net')) {
        return true;
    }
    // For other domains (like bellomyonline.com subdomains), use cookies
    return false;
}
/**
 * Gets the root domain for cookie sharing (e.g., '.bellomyonline.com')
 */
function getCookieDomain() {
    if (typeof window === 'undefined')
        return '';
    const hostname = window.location.hostname;
    // For localhost, cookies don't work across ports, so we return empty
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return '';
    }
    // For production, extract the root domain (e.g., 'app.bellomyonline.com' -> '.bellomyonline.com')
    const parts = hostname.split('.');
    if (parts.length >= 2) {
        return '.' + parts.slice(-2).join('.');
    }
    return hostname;
}
/**
 * Creates a URL with authentication token for cross-app navigation
 *
 * @param targetUrl - The URL to navigate to
 * @returns URL with authentication token appended (for development) or original URL (for production with cookie)
 */
export function createAuthenticatedUrl(targetUrl, workspaceId) {
    if (typeof window === 'undefined')
        return targetUrl;
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
        return targetUrl;
    }
    // Verify token is not expired before including it
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expirationTime = payload.exp * 1000;
        if (Date.now() >= expirationTime) {
            return targetUrl;
        }
    }
    catch {
        console.error('CrossDomainAuth: Could not parse token, returning original URL');
        return targetUrl;
    }
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    return createAuthenticatedUrlForTransferData(targetUrl, buildTransferData(token, !isImpersonationToken(token) ? refreshToken || undefined : undefined, workspaceId));
}
/**
 * Sets a cross-subdomain cookie with authentication data
 */
function setCrossDomainCookieData(data) {
    const domain = getCookieDomain();
    const cookieValue = btoa(JSON.stringify(data));
    const expirySeconds = TOKEN_EXPIRY_MINUTES * 60;
    // Build cookie string with appropriate attributes
    let cookieString = `${CROSS_DOMAIN_COOKIE_NAME}=${cookieValue}; path=/; max-age=${expirySeconds}; SameSite=None; Secure`;
    if (domain) {
        cookieString += `; domain=${domain}`;
    }
    document.cookie = cookieString;
}
export function createAuthenticatedUrlForToken(targetUrl, token, refreshToken, workspaceId) {
    return createAuthenticatedUrlForTransferData(targetUrl, buildTransferData(token, !isImpersonationToken(token) ? refreshToken : undefined, workspaceId));
}
function createAuthenticatedUrlForTransferData(targetUrl, data) {
    if (shouldUseUrlParams()) {
        if (isImpersonationToken(data.token)) {
            throw new Error('Impersonation handoff requires a shared cookie-capable domain');
        }
        const url = new URL(targetUrl);
        url.searchParams.set(CROSS_DOMAIN_PARAM_NAME, data.token);
        if (data.refreshToken && !isImpersonationToken(data.token)) {
            url.searchParams.set('refresh_token', data.refreshToken);
        }
        if (data.workspaceId) {
            url.searchParams.set(CROSS_DOMAIN_WORKSPACE_PARAM_NAME, data.workspaceId);
        }
        url.searchParams.set('auth_ts', data.timestamp.toString());
        return url.toString();
    }
    setCrossDomainCookieData(data);
    return targetUrl;
}
/**
 * Retrieves and validates cross-domain authentication from URL params or cookies
 * Call this on app initialization to check for incoming auth transfer
 *
 * @returns true if authentication was found and applied, false otherwise
 */
export function checkForCrossDomainAuth() {
    if (typeof window === 'undefined')
        return false;
    // First, check URL parameters (for development)
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get(CROSS_DOMAIN_PARAM_NAME);
    const workspaceIdFromUrl = urlParams.get(CROSS_DOMAIN_WORKSPACE_PARAM_NAME);
    const timestampFromUrl = urlParams.get('auth_ts');
    const refreshTokenFromUrl = urlParams.get('refresh_token');
    if (tokenFromUrl && timestampFromUrl) {
        // Validate timestamp (must be within TOKEN_EXPIRY_MINUTES)
        const timestamp = parseInt(timestampFromUrl, 10);
        const now = Date.now();
        const maxAge = TOKEN_EXPIRY_MINUTES * 60 * 1000;
        if (now - timestamp > maxAge) {
            cleanUrlParams();
            return false;
        }
        // Validate the token itself
        if (!isTokenValid(tokenFromUrl)) {
            cleanUrlParams();
            return false;
        }
        // Apply the token
        localStorage.setItem(TOKEN_KEY, tokenFromUrl);
        if (isImpersonationToken(tokenFromUrl)) {
            localStorage.removeItem(REFRESH_TOKEN_KEY);
            localStorage.setItem(IMPERSONATION_MODE_KEY, 'true');
        }
        else if (refreshTokenFromUrl) {
            localStorage.setItem(REFRESH_TOKEN_KEY, refreshTokenFromUrl);
            localStorage.removeItem(IMPERSONATION_MODE_KEY);
        }
        else {
            localStorage.removeItem(IMPERSONATION_MODE_KEY);
        }
        if (workspaceIdFromUrl) {
            localStorage.setItem(TARGET_WORKSPACE_KEY, workspaceIdFromUrl);
        }
        else {
            localStorage.removeItem(TARGET_WORKSPACE_KEY);
        }
        // Clean up URL parameters without triggering a page reload
        cleanUrlParams();
        return true;
    }
    // Then, check for cross-domain cookie (for production)
    const cookieData = getCrossDomainCookie();
    if (cookieData) {
        // Validate timestamp
        const now = Date.now();
        const maxAge = TOKEN_EXPIRY_MINUTES * 60 * 1000;
        if (now - cookieData.timestamp > maxAge) {
            clearCrossDomainCookie();
            return false;
        }
        // Validate the token itself
        if (!isTokenValid(cookieData.token)) {
            clearCrossDomainCookie();
            return false;
        }
        // Apply the token
        localStorage.setItem(TOKEN_KEY, cookieData.token);
        if (isImpersonationToken(cookieData.token)) {
            localStorage.removeItem(REFRESH_TOKEN_KEY);
            localStorage.setItem(IMPERSONATION_MODE_KEY, 'true');
        }
        else if (cookieData.refreshToken) {
            localStorage.setItem(REFRESH_TOKEN_KEY, cookieData.refreshToken);
            localStorage.removeItem(IMPERSONATION_MODE_KEY);
        }
        else {
            localStorage.removeItem(IMPERSONATION_MODE_KEY);
        }
        if (cookieData.workspaceId) {
            localStorage.setItem(TARGET_WORKSPACE_KEY, cookieData.workspaceId);
        }
        else {
            localStorage.removeItem(TARGET_WORKSPACE_KEY);
        }
        // Clear the cookie after use for security
        clearCrossDomainCookie();
        return true;
    }
    return false;
}
/**
 * Validates a JWT token
 */
function isTokenValid(token) {
    try {
        const parts = token.split('.');
        if (parts.length !== 3)
            return false;
        const payload = JSON.parse(atob(parts[1]));
        const expirationTime = payload.exp * 1000;
        return Date.now() < expirationTime;
    }
    catch (e) {
        console.error('CrossDomainAuth: Error validating token:', e);
        return false;
    }
}
/**
 * Gets cross-domain cookie data
 */
function getCrossDomainCookie() {
    if (typeof document === 'undefined')
        return null;
    const cookies = document.cookie.split(';');
    const authCookie = cookies.find(cookie => cookie.trim().startsWith(`${CROSS_DOMAIN_COOKIE_NAME}=`));
    if (!authCookie)
        return null;
    try {
        const cookieValue = authCookie.split('=')[1].trim();
        const data = JSON.parse(atob(cookieValue));
        return data;
    }
    catch (e) {
        console.error('CrossDomainAuth: Error parsing cookie:', e);
        return null;
    }
}
/**
 * Clears the cross-domain authentication cookie
 */
function clearCrossDomainCookie() {
    if (typeof document === 'undefined')
        return;
    const domain = getCookieDomain();
    // Clear with domain
    if (domain) {
        document.cookie = `${CROSS_DOMAIN_COOKIE_NAME}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=${domain}; SameSite=None; Secure`;
    }
    // Also clear without domain (in case it was set that way)
    document.cookie = `${CROSS_DOMAIN_COOKIE_NAME}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=None; Secure`;
}
/**
 * Removes auth parameters from URL without page reload
 */
function cleanUrlParams() {
    if (typeof window === 'undefined')
        return;
    const url = new URL(window.location.href);
    url.searchParams.delete(CROSS_DOMAIN_PARAM_NAME);
    url.searchParams.delete(CROSS_DOMAIN_WORKSPACE_PARAM_NAME);
    url.searchParams.delete('refresh_token');
    url.searchParams.delete('auth_ts');
    // Use replaceState to update URL without reload
    window.history.replaceState({}, '', url.toString());
}
/**
 * Navigates to another app with authentication
 * Use this instead of regular <a href> or window.location for cross-app navigation
 *
 * @param targetUrl - The URL to navigate to
 */
export function navigateWithAuth(targetUrl, workspaceId) {
    const authenticatedUrl = createAuthenticatedUrl(targetUrl, workspaceId);
    window.location.href = authenticatedUrl;
}
/**
 * React hook-friendly function to handle link clicks with auth transfer
 * Use this with onClick handlers
 *
 * @param e - Click event
 * @param targetUrl - The URL to navigate to
 */
export function handleAuthenticatedNavigation(e, targetUrl) {
    e.preventDefault();
    navigateWithAuth(targetUrl);
}
export default {
    createAuthenticatedUrl,
    checkForCrossDomainAuth,
    navigateWithAuth,
    handleAuthenticatedNavigation,
};
//# sourceMappingURL=crossDomainAuth.js.map